import { Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useInsightsHeadcountDataTable,
} from 'generated/resources/datatable.graphql'

import { dateBackendDateTimeToUI, dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

export enum HeadcountViewContentTab {
  Headcount = 'headcount',
  Joiners = 'joiners',
  Leavers = 'leavers',
}

export const useHeadcountDatatableSource = (
  tab: HeadcountViewContentTab,
  employeeIds: number[],
  currentDate: Date
) => {
  return useInsightsHeadcountDataTable(
    [
      {
        identifier: 'fullName',
        kind: ProjectionKind.Path,
        title: i18n.t('insights.headcount_granular_view.table.headers.full_name'),
        path: 'fullName',
      },
      {
        identifier: 'startDate',
        kind: ProjectionKind.Custom,
        title: i18n.t('insights.headcount_granular_view.table.headers.start_date'),
        template: '{{ contractversionsConnection.nodes[0].startsOn }}',
        render: (employee) => {
          const contractStartDate = employee.contractversionsConnection.nodes[0]?.startsOn

          return (
            <Text.Normal>
              {contractStartDate && dateBackendDateTimeToUI(contractStartDate)}
            </Text.Normal>
          )
        },
        hidden: tab === HeadcountViewContentTab.Leavers,
      },
      {
        identifier: 'endDate',
        kind: ProjectionKind.Custom,
        title: i18n.t('insights.headcount_granular_view.table.headers.end_date'),
        template: `
          {% assign endDate = contractversionsConnection.nodes[0].endsOn %}

          {% if terminatedOn != nil %}
            {% if endDate == nil %}
              {% assign endDate = terminatedOn %}
            {% elsif terminatedOn < endDate %}
              {% assign endDate = terminatedOn %}
            {% endif %}
          {% endif %}

          {{ endDate }}
        `
          .replace(/\s+\{/g, '{')
          .replace(/\}\s+/g, '}'), // Required to keep indentation without adding spaces to the template result
        render: (employee) => {
          // Render the smaller non-null date between the termination date and the contract end date
          const contractEndDate = employee.contractversionsConnection.nodes[0]?.endsOn

          let endDate = contractEndDate

          if (employee.terminatedOn) {
            if (!endDate) {
              endDate = employee.terminatedOn
            } else if (employee.terminatedOn < endDate) {
              endDate = employee.terminatedOn
            }
          }

          return <Text.Normal>{endDate && dateBackendDateTimeToUI(endDate)}</Text.Normal>
        },
        hidden: tab !== HeadcountViewContentTab.Leavers,
      },
      {
        identifier: 'terminationReason',
        kind: ProjectionKind.Custom,
        title: i18n.t('insights.headcount_granular_view.table.headers.termination_reason'),
        template: `
          {% if terminationReason != nil %}
            {{ terminationReason }}
          {% elsif terminationTypeDescription != nil %}
            {{ terminationTypeDescription }}
          {% endif %}
        `
          .replace(/\s+\{/g, '{')
          .replace(/\}\s+/g, '}'), // Required to keep indentation without adding spaces to the template result
        render: (employee) => {
          return (
            <Text.Normal>
              {employee.terminationReason || employee.terminationTypeDescription}
            </Text.Normal>
          )
        },
        hidden: tab !== HeadcountViewContentTab.Leavers,
      },
      {
        identifier: 'managerFullName',
        kind: ProjectionKind.Path,
        title: i18n.t('insights.headcount_granular_view.table.headers.manager'),
        path: 'manager_fullName',
      },
    ],
    {
      variables: {
        employeeIds: employeeIds,
        contractDate: dateToString(currentDate, 'backend'),
      },
    }
  )
}
